import { Logger } from '@unistocks/shared/services/logger.service';
import { isWeb } from 'tamagui';

/**
 * ============================================================================
 * Color from AmCharts 5
 * ============================================================================
 */

/**
 * ============================================================================
 * COLOR UTILS
 * ============================================================================
 */

/**
 * Represents an interface for an object that represents an RGB color.
 */
export interface RGB {
  r: number;
  g: number;
  b: number;
  a?: number;
}

/**
 * Represents an interface for an object that represents an HSL color.
 */
export interface HSL {
  h: number;
  s: number;
  l: number;
  a?: number;
}

/**
 * The functions below are taken and adapted from Garry Tan's blog post:
 * http://axonflux.com/handy-rgb-to-hsl-and-rgb-to-hsv-color-model-c
 *
 * The further attributions go mjijackson.com, which now seems to be defunct.
 */

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * Function adapted from:
 * http://axonflux.com/handy-rgb-to-hsl-and-rgb-to-hsv-color-model-c
 *
 * @ignore Exclude from docs
 * @param h       The hue
 * @param s       The saturation
 * @param l       The lightness
 * @return The RGB representation
 */
export function hslToRgb({ h, s, l }: HSL): RGB {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = function hue2rgb(p: number, q: number, t: number) {
      if (t < 0) {
        t += 1;
      }
      if (t > 1) {
        t -= 1;
      }
      if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
      }
      if (t < 1 / 2) {
        return q;
      }
      if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
      }
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

/**
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 *
 * Function adapted from:
 * http://axonflux.com/handy-rgb-to-hsl-and-rgb-to-hsv-color-model-c
 *
 * @ignore Exclude from docs
 * @param r       The red color value
 * @param g       The green color value
 * @param b       The blue color value
 * @return The HSL representation
 */
export function rgbToHsl(color: RGB): HSL {
  const r = color.r / 255;
  const g = color.g / 255;
  const b = color.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0,
    s = 0;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;

    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return {
    h: h,
    s: s,
    l: l
  };
}

// Regular expression to match a hsla() color string
const hslStringPattern =
  /hsla?\(\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)%\s*,\s*(\d+\.?\d*)%\s*(?:,\s*(\d+\.?\d*)\s*)?\)/i;

export function hslStringToHsl(hslString: string): HSL {
  const match = hslString.match(hslStringPattern);

  if (!match) {
    throw new Error('Invalid HSL(A) color string format.');
  }

  // Parse the matched groups into H, S, L, and optionally A components
  const h = parseFloat(match[1]) / 360;
  const s = parseFloat(match[2]) / 100;
  const l = parseFloat(match[3]) / 100;
  const a = match[4] !== undefined ? parseFloat(match[4]) : 1.0;

  return { h, s, l, a };
}

/**
 * Returns a color that is `percent` brighter than the reference color.
 *
 * @ignore Exclude from docs
 * @param color    Reference color
 * @param percent  Brightness percent
 * @return Hex code of the new color
 */
export function lighten(rgb: RGB, percent: number): RGB {
  if (rgb) {
    return {
      r: Math.max(0, Math.min(255, rgb.r + getLightnessStep(rgb.r, percent))),
      g: Math.max(0, Math.min(255, rgb.g + getLightnessStep(rgb.g, percent))),
      b: Math.max(0, Math.min(255, rgb.b + getLightnessStep(rgb.b, percent))),
      a: rgb.a
    };
  } else {
    // TODO is this correct ?
    return rgb;
  }
}

/**
 * Gets lightness step.
 *
 * @ignore Exclude from docs
 * @param value    Value
 * @param percent  Percent
 * @return Step
 */
export function getLightnessStep(value: number, percent: number): number {
  const base = percent > 0 ? 255 - value : value;
  return Math.round(base * percent);
}

/**
 * Returns a color that is `percent` brighter than the source `color`.
 *
 * @ignore Exclude from docs
 * @param color    Source color
 * @param percent  Brightness percent
 * @return New color
 */
export function brighten(rgb: RGB, percent: number): RGB {
  if (rgb) {
    const base = Math.min(Math.max(rgb.r, rgb.g, rgb.b), 230);
    //let base = Math.max(rgb.r, rgb.g, rgb.b);
    const step = getLightnessStep(base, percent);
    return {
      r: Math.max(0, Math.min(255, Math.round(rgb.r + step))),
      g: Math.max(0, Math.min(255, Math.round(rgb.g + step))),
      b: Math.max(0, Math.min(255, Math.round(rgb.b + step))),
      a: rgb.a
    };
  } else {
    // TODO is this correct ?
    return rgb;
  }
}

/**
 * Returns brightness step.
 *
 * @ignore Exclude from docs
 * @param value    Value
 * @param percent  Percent
 * @return Step
 */
export function getBrightnessStep(_value: number, percent: number): number {
  const base = 255; //percent > 0 ? 255 - value : value;
  return Math.round(base * percent);
}

/**
 * Returns `true` if color is "light". Useful indetermining which contrasting
 * color to use for elements over this color. E.g.: you would want to use
 * black text over light background, and vice versa.
 *
 * @ignore Exclude from docs
 * @param color  Source color
 * @return Light?
 */
export function isLight(color: RGB): boolean {
  return (color.r * 299 + color.g * 587 + color.b * 114) / 1000 >= 128;
}

/**
 * Returns a new [[RGB]] object based on `rgb` parameter with specific
 * saturation applied.
 *
 * `saturation` can be in the range of 0 (fully desaturated) to 1 (fully
 * saturated).
 *
 * @ignore Exclude from docs
 * @param color       Base color
 * @param saturation  Saturation (0-1)
 * @return New color
 */
export function saturate(rgb: RGB, saturation: number): RGB {
  if (rgb === undefined || saturation === 1) {
    return rgb;
  }

  const hsl = rgbToHsl(rgb);
  hsl.s = saturation;
  return hslToRgb(hsl);
}

export function alternativeColor(
  color: RGB,
  lightAlternative: RGB = { r: 255, g: 255, b: 255 },
  darkAlternative: RGB = { r: 255, g: 255, b: 255 }
): RGB {
  let light = lightAlternative;
  let dark = darkAlternative;
  if (isLight(darkAlternative)) {
    light = darkAlternative;
    dark = lightAlternative;
  }
  return isLight(color) ? dark : light;
}

function string2hex(string: string): number {
  //string = cssColorNames[string.toLowerCase()] || string;

  if (string[0] === '#') {
    string = string.substring(1);
  }
  if (string.length === 3) {
    string = string[0].repeat(2) + string[1].repeat(2) + string[2].repeat(2);
  }

  return parseInt(string, 16);
}

export function rgba2hex(color: string): number {
  color = color.replace(/[ ]/g, '');

  // Init
  let matches: string[] | null = color.match(/^rgb\(([0-9]*),([0-9]*),([0-9]*)\)/i);

  // Try rgb() format
  if (matches) {
    matches.push('1');
  } else {
    matches = color.match(/^rgba\(([0-9]*),([0-9]*),([0-9]*),([.0-9]*)\)/i);

    if (!matches) {
      return 0x000000;
    }
  }

  let hex = '';
  for (let i = 1; i <= 3; i++) {
    let val = parseInt(matches[i]).toString(16);
    if (val.length === 1) {
      val = '0' + val;
    }
    hex += val;
  }

  return string2hex(hex);
}

/**
 * Pads a string with additional characters to certain length.
 *
 * @param value  A numeric value
 * @param len    Result string length in characters
 * @param char   A character to use for padding
 * @return Padded value as string
 */
export function padString(value: any, len = 0, char = '0'): string {
  if (typeof value !== 'string') {
    value = value.toString();
  }

  return len > value.length ? Array(len - value.length + 1).join(char) + value : value;
}

export function range(diff: number, from: number, to: number): number {
  return from + diff * (to - from);
}

/**
 * Returns a new [[Color]] object base on input.
 *
 * Accepts parameters in CSS hex or rgb/rtba strings, or hex numbers.
 *
 * * `"#f00"`
 * * `"#ff0000"`
 * * `"rgb(255, 0, 0)"`
 * * `"rgba(255, 0, 0, 1)"`
 * * `0xff0000`
 *
 * @param   input  Input color
 * @return         Color
 */
export function color(input: number | string): Color {
  return Color.fromAny(input);
}

export class Color {
  protected _hex: number;

  private constructor(hex: number) {
    this._hex = hex | 0;
  }

  /**
   * Color numeric value.
   */
  public get hex(): number {
    return this._hex;
  }

  /**
   * Value of color's R channel.
   * @return R value
   */
  public get r(): number {
    return this._hex >>> 16;
  }

  /**
   * Value of color's G channel.
   * @return G value
   */
  public get g(): number {
    return (this._hex >> 8) & 0xff;
  }

  /**
   * Value of color's B channel.
   * @return B value
   */
  public get b(): number {
    return this._hex & 0xff;
  }

  /**
   * Returns color CSS representation in form of `rgba(r, g, b, a)` string.
   *
   * @param   alpha  Opacity
   * @return         CSS string
   */
  public toCSS(alpha: number = 1): string {
    return 'rgba(' + this.r + ', ' + this.g + ', ' + this.b + ', ' + alpha + ')';
  }

  /**
   * Returns color CSS representation in form of `#rgb` string.
   *
   * @return         CSS string
   */
  public toCSSHex(): string {
    return (
      '#' +
      padString(this.r.toString(16), 2) +
      padString(this.g.toString(16), 2) +
      padString(this.b.toString(16), 2)
    );
  }

  /**
   * Returns color's HSL info.
   * @param   alpha Opacity
   * @return        HSL info
   */
  public toHSL(alpha: number = 1): HSL {
    return rgbToHsl({
      r: this.r,
      g: this.g,
      b: this.b,
      a: alpha
    });
  }

  /**
   * Converts HSL values into a new [[Color]] object.
   *
   * @param   h H value
   * @param   s S value
   * @param   l L value
   * @return    Color object
   */
  public static fromHSL(h: number, s: number, l: number): Color {
    const rgb = hslToRgb({
      h: h,
      s: s,
      l: l
    });
    return this.fromRGB(rgb.r, rgb.g, rgb.b);
  }

  public toString(): string {
    return this.toCSSHex();
  }

  /**
   * Converts hex number into a new [[Color]] object.
   *
   * ```TypeScript
   * Color.fromHex(0xff0000) // red
   * ```
   * ```JavaScript
   * Color.fromHex(0xff0000) // red
   * ```
   *
   * @param   hex  Hex color
   * @return       Color
   */
  public static fromHex(hex: number): Color {
    return new Color(hex);
  }

  /**
   * Converts RGB values to a new [[Color]] object.
   *
   * @param   r  R value
   * @param   g  G value
   * @param   b  B value
   * @return     Color
   */
  public static fromRGB(r: number, g: number, b: number): Color {
    return new Color((b | 0) + (g << 8) + (r << 16));
  }

  /**
   * Converts RGB string to a new [[Color]] object.
   *
   * ```TypeScript
   * Color.fromString("#ff0000") // red
   * ```
   * ```JavaScript
   * Color.fromString("#ff0000") // red
   * ```
   *
   * @param   s  RGB string
   * @return     Color
   */
  public static fromString(s: string): Color {
    return new Color(string2hex(s));
  }

  /**
   * Converts CSS rgba() syntax to a new [[Color]] object.
   *
   * ```TypeScript
   * Color.fromCSS("rgba(255, 0, 0, 1)") // red
   * ```
   * ```JavaScript
   * Color.fromCSS("rgba(255, 0, 0, 1)") // red
   * ```
   *
   * @param  {string} s [description]
   * @return {Color}    [description]
   */
  public static fromCSS(s: string): Color {
    return new Color(rgba2hex(s));
  }

  /**
   * Convert to color from virtually anything.
   *
   * Will throw an exception if unable to resolve the color.
   *
   * @param   s  Source
   * @return     Color
   */
  public static fromAny(s: string | number | Color): Color {
    if (typeof s === 'string') {
      // TODO: Check logs. Can this be deleted?
      if (isWeb && s.startsWith('var(')) {
        const cssVariableName = s.slice(4, -1).trim();
        const value = window
          .getComputedStyle(document.documentElement)
          .getPropertyValue(cssVariableName)
          ?.trim();
        Logger.verbose('CSS property transform', s, value);

        return this.fromAny(value);
      }
      if (s[0] === '#') {
        return Color.fromString(s);
      } else if (s.substring(0, 3) === 'rgb') {
        return Color.fromCSS(s);
      } else if (hslStringPattern.test(s)) {
        const hsl = hslStringToHsl(s);
        if (hsl) {
          return Color.fromHSL(hsl.h, hsl.s, hsl.l);
        }
      }
    } else if (typeof s === 'number' && !isNaN(s)) {
      return Color.fromHex(s);
    } else if (s instanceof Color) {
      return Color.fromHex(s.hex);
    }

    throw new Error('Unknown color syntax: ' + s);
  }

  /**
   * Returns a new [[Color]] object based on either `lightAlternative` or
   * `darkAlternative` depending on which one is more contrasting with
   * the `color`.
   *
   * @param   color             Reference color
   * @param   lightAlternative  Light color
   * @param   darkAlternative   Dark color
   * @return                    Alternative color
   */
  public static alternative(
    color: Color,
    lightAlternative?: Color,
    darkAlternative?: Color
  ): Color {
    const rgb = alternativeColor(
      { r: color.r, g: color.g, b: color.b },
      lightAlternative
        ? { r: lightAlternative.r, g: lightAlternative.g, b: lightAlternative.b }
        : undefined,
      darkAlternative
        ? { r: darkAlternative.r, g: darkAlternative.g, b: darkAlternative.b }
        : undefined
    );
    return this.fromRGB(rgb.r, rgb.g, rgb.b);
  }

  /**
   * Returns an intermediate Color between two reference colors depending on
   * the progress (`diff`) between the two.
   *
   * @param   diff  Progress
   * @param   from  Source color
   * @param   to    Target color
   * @param   mode  Interpolation mode
   * @return        Color
   */
  public static interpolate(
    diff: number,
    from: Color,
    to: Color,
    mode: 'rgb' | 'hsl' = 'rgb'
  ): Color {
    if (mode === 'hsl') {
      const fromHSL = from.toHSL();
      const toHSL = to.toHSL();
      return Color.fromHSL(
        range(diff, fromHSL.h, toHSL.h),
        range(diff, fromHSL.s, toHSL.s),
        range(diff, fromHSL.l, toHSL.l)
      );
    } else {
      return Color.fromRGB(
        range(diff, from.r, to.r),
        range(diff, from.g, to.g),
        range(diff, from.b, to.b)
      );
    }
  }

  /**
   * Returns a new [[Color]] lightened by `percent` value.
   *
   * Use negative value to darken the color.
   *
   * @param   color    Source color
   * @param   percent  Percent
   * @return           New color
   */
  public static lighten(color: Color, percent: number): Color {
    const rgb = lighten({ r: color.r, g: color.g, b: color.b }, percent);
    return Color.fromRGB(rgb!.r, rgb!.g, rgb!.b);
  }

  /**
   * Returns a new [[Color]] lightened by `percent` value.
   *
   * Use negative value to darken the color.
   *
   * @param   color    Source color
   * @param   percent  Percent
   * @return           New color
   */
  public lighten(percent: number): Color {
    return Color.lighten(this, percent);
  }

  public darken(percent: number): Color {
    return Color.lighten(this, -percent);
  }

  /**
   * Returns a new [[Color]] brightened by `percent` value.
   *
   * Use negative value to dim the color.
   *
   * @param   color    Source color
   * @param   percent  Percent
   * @return           New color
   */
  public static brighten(color: Color, percent: number): Color {
    const rgb = brighten({ r: color.r, g: color.g, b: color.b }, percent);
    return Color.fromRGB(rgb!.r, rgb!.g, rgb!.b);
  }

  /**
   * Returns a new [[Color]] brightened by `percent` value.
   *
   * Use negative value to dim the color.
   *
   * @param   percent  Percent
   * @return           New color
   */
  public brighten(percent: number): Color {
    return Color.brighten(this, percent);
  }

  /**
   * Returns a new [[Color]] saturated by `percent` value.
   *
   * Value range is between `0` (fully desaturated), to `1` (full color).
   *
   * @param   color    Source color
   * @param   percent  Percent
   * @return           New color
   */
  public static saturate(color: Color, percent: number): Color {
    const rgb = saturate({ r: color.r, g: color.g, b: color.b }, percent);
    return Color.fromRGB(rgb!.r, rgb!.g, rgb!.b);
  }
}
